// 评价状态持久化：localStorage 单一状态树
//
// 替代旧实现的 16 个 sessionStorage 键位 + 逐个 watch 的写法。
// 统一为一个 JSON 键，规避 "null" / "undefined" 字符串 truthy 陷阱，
// 并支持刷新/重载后自动续填。

import { DEFAULT_WEIGHTS } from '../data/index.js'

export const STORAGE_KEY = 'ma-eval-v4'
export const STATE_VERSION = 4

/** 阶段流转：intro → home → role → self → wizard → result */
export const STAGES = ['intro', 'home', 'role', 'self', 'wizard', 'result']

export function createDefaultState () {
  return {
    version: STATE_VERSION,
    stage: 'intro',
    scope: null,        // 'project' | 'enterprise'
    edition: 'online',  // 'online' | 'full'
    roleId: null,       // 三方十类角色 id
    subjectName: '',    // 项目名称 / 企业名称
    selfLevel: '',      // 自评等级预判（可选项，L0-L5）
    step: 0,            // 当前能力域步序 0-5
    weights: { ...DEFAULT_WEIGHTS },
    // 分数按 scope:edition 命名空间隔离：
    // 快速版与完整版的指标 id 会重叠（如 arch-4 在两版中含义不同），切换版本不能串分
    scores: {},
    savedAt: null
  }
}

export function scoresKey (scope, edition) {
  return `${scope || ''}:${edition || ''}`
}

// 空袋子常量：getScores 不回写 state，避免在 computed 中修改响应式对象
// （computed 里写 state 会让依赖自触发，且新键不会按预期失效缓存）
const EMPTY_SCORES = Object.freeze({})

export function getScores (state, scope, edition) {
  if (!state) return EMPTY_SCORES
  return state.scores[scoresKey(scope, edition)] || EMPTY_SCORES
}

/** 读取状态；结构与版本不兼容时回退到初始状态 */
export function loadState () {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return createDefaultState()
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== STATE_VERSION) return createDefaultState()
    return { ...createDefaultState(), ...parsed }
  } catch (e) {
    return createDefaultState()
  }
}

export function saveState (state) {
  try {
    // savedAt 只写入落盘副本，不回写 state：
    // 否则 deep watch(state) → saveState → 改 savedAt → 再次触发 watch，形成死循环
    const payload = { ...state, savedAt: new Date().toISOString() }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (e) {
    // 隐私模式或配额不足时静默降级为内存态
  }
  return state
}

export function clearState () {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) { /* 忽略 */ }
  return createDefaultState()
}

/** 写入单个指标的评分；level 为 null 表示清除 */
export function setIndicatorScore (state, scope, edition, indicatorId, patch) {
  const key = scoresKey(scope, edition)
  if (!state.scores[key]) state.scores[key] = {}
  const bag = state.scores[key]
  const cur = bag[indicatorId] || { level: null, answers: {}, note: '', files: [] }
  bag[indicatorId] = { ...cur, ...patch }
  return state
}

export function resetScores (state, scope, edition) {
  state.scores[scoresKey(scope, edition)] = {}
  return state
}

/**
 * 找出第一份有评分进度的评价（跨 scope:edition 查找）
 * 用户可能中途切换了评价范围/深度，只看当前组合会漏掉已填的那份
 */
export function findFirstProgress (state) {
  if (!state || !state.scores) return null
  for (const key of Object.keys(state.scores)) {
    const bag = state.scores[key] || {}
    const rated = Object.values(bag).some((v) => v && typeof v.level === 'number')
    if (rated) {
      const [scope, edition] = key.split(':')
      if (scope && edition) return { scope, edition }
    }
  }
  return null
}

/** 是否已有可续填的评价进度 */
export function hasProgress (state) {
  return !!findFirstProgress(state)
}
